/**
 * "Already reported" as a badge — VH-140.
 *
 * Colour is never the only signal. Every state carries an icon AND words:
 * colour alone fails for colour-blind users and against the WCAG AA floor
 * VH-133 enforces in `contrast.test.js`. The colour is there to make a long
 * sheet scannable, not to carry the meaning.
 *
 * Uses the semantic tokens (`success`, `warning`, `on-surface-variant`) rather
 * than raw hues, so it follows both themes. Deliberately no tinted row
 * background: the `-50` rungs read brown on the violet canvas, which is VH-135's
 * still-open cosmetic issue, and this feature should not add to it.
 */
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, ClockIcon, MinusCircleIcon } from '@heroicons/react/24/outline'

const STYLES = {
  full: { Icon: CheckCircleIcon, cls: 'text-success-600', key: 'captureProgress.reported' },
  partial: { Icon: ClockIcon, cls: 'text-warning-600', key: 'captureProgress.partial' },
  none: { Icon: MinusCircleIcon, cls: 'text-gray-500', key: 'captureProgress.notReported' },
}

/**
 * @param {'full'|'partial'|'none'} state
 * @param {number} [reported] filled count, shown for the partial state
 * @param {number} [total]
 */
export default function ReportedBadge({ state, reported, total, className = '' }) {
  const { t } = useTranslation()
  const style = STYLES[state] ?? STYLES.none
  const { Icon } = style

  const label =
    state === 'partial' && total
      ? t('captureProgress.partialCount', { reported, total })
      : t(style.key)

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${style.cls} ${className}`}
      data-testid={`reported-badge-${state}`}
      // Announced as one phrase; the icon is decorative because the text says it.
      aria-label={label}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {label}
    </span>
  )
}
